import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, CheckCircle2, XCircle, Search, ArrowLeft, ArrowRight, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface UserInfo {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface ResendInvitationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymId: string;
  gymName: string;
  appName?: string | null;
}

type Step = "select" | "preview" | "result";

export function ResendInvitationsDialog({
  open,
  onOpenChange,
  gymId,
  gymName,
  appName,
}: ResendInvitationsDialogProps) {
  const { t } = useTranslation("superadmin");
  const [step, setStep] = useState<Step>("select");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<{
    total_sent: number;
    total_failed: number;
    failed: { user_id: string; email: string; error: string }[];
  } | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep("select");
      setUsers([]);
      setSelectedUserIds(new Set());
      setSearchQuery("");
      setConfirmed(false);
      setResult(null);
      loadUsers();
    }
  }, [open, gymId]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("resend-invitations", {
        body: { gym_id: gymId, dry_run: true },
      });

      if (error) throw error;
      if (data.users) {
        setUsers(data.users);
        // Select all by default
        setSelectedUserIds(new Set(data.users.map((u: UserInfo) => u.user_id)));
      }
    } catch (err) {
      console.error("Error loading users:", err);
      toast({
        title: t("resendInvitations.error"),
        description: t("resendInvitations.loadUsersError"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      (user.first_name?.toLowerCase() || "").includes(query) ||
      (user.last_name?.toLowerCase() || "").includes(query)
    );
  });

  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map((u) => u.user_id)));
    }
  };

  const toggleUser = (userId: string) => {
    const newSet = new Set(selectedUserIds);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setSelectedUserIds(newSet);
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("resend-invitations", {
        body: {
          gym_id: gymId,
          user_ids: Array.from(selectedUserIds),
          dry_run: false,
        },
      });

      if (error) throw error;

      setResult({
        total_sent: data.total_sent,
        total_failed: data.total_failed,
        failed: data.failed || [],
      });
      setStep("result");

      if (data.total_sent > 0) {
        toast({
          title: t("resendInvitations.success"),
          description: t("resendInvitations.emailsSentDesc", { count: data.total_sent }),
        });
      }
    } catch (err) {
      console.error("Error sending invitations:", err);
      toast({
        title: t("resendInvitations.error"),
        description: t("resendInvitations.sendError"),
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const renderSelectStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          {t("resendInvitations.title")}
        </DialogTitle>
        <DialogDescription>
          {gymName} {appName && <Badge variant="outline" className="ml-2">{appName}</Badge>}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("resendInvitations.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 py-2 border-b">
              <Checkbox
                id="select-all"
                checked={selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                {t("resendInvitations.selectAll", { count: filteredUsers.length })}
              </label>
            </div>

            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {filteredUsers.map((user) => (
                  <div
                    key={user.user_id}
                    className="flex items-center gap-3 p-2 rounded-md hover:bg-accent cursor-pointer"
                    onClick={() => toggleUser(user.user_id)}
                  >
                    <Checkbox
                      checked={selectedUserIds.has(user.user_id)}
                      onCheckedChange={() => toggleUser(user.user_id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {user.first_name || user.last_name
                          ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                          : t("resendInvitations.noName")}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("resendInvitations.cancel")}
          </Button>
          <Button
            onClick={() => setStep("preview")}
            disabled={selectedUserIds.size === 0}
          >
            {t("resendInvitations.preview", { count: selectedUserIds.size })}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </>
  );

  const renderPreviewStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          {t("resendInvitations.previewTitle")}
        </DialogTitle>
        <DialogDescription>{gymName}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="bg-status-warning text-status-warning-foreground border border-status-warning-border rounded-lg p-4">
          <p className="text-sm font-medium">
            {t("resendInvitations.willSend", { count: selectedUserIds.size })}
          </p>
        </div>

        <div className="space-y-2 text-sm">
          <p className="text-muted-foreground">{t("resendInvitations.willReceive")}</p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>{t("resendInvitations.welcomeEmail")}</li>
            <li>{t("resendInvitations.passwordLink")}</li>
            <li>{t("resendInvitations.linkValidity")}</li>
          </ul>
        </div>

        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">{t("resendInvitations.redirectUrl")}</p>
          <code className="text-xs break-all">
            https://core.stryvfit.app/set-password?appName={appName || "StriFit"}
          </code>
        </div>

        <div className="flex items-center gap-2 py-2">
          <Checkbox
            id="confirm-send"
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(checked === true)}
          />
          <label htmlFor="confirm-send" className="text-sm cursor-pointer">
            {t("resendInvitations.confirmSend", { count: selectedUserIds.size })}
          </label>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => setStep("select")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("resendInvitations.back")}
          </Button>
          <Button
            onClick={handleSend}
            disabled={!confirmed || isSending}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("resendInvitations.sending")}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {t("resendInvitations.send")}
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );

  const renderResultStep = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {result && result.total_failed === 0 ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-amber-500" />
          )}
          {t("resendInvitations.sendComplete")}
        </DialogTitle>
        <DialogDescription>{gymName}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-status-success text-status-success-foreground border border-status-success-border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold">
              {result?.total_sent || 0}
            </p>
            <p className="text-sm">{t("resendInvitations.emailsSent")}</p>
          </div>
          <div className="bg-status-danger text-status-danger-foreground border border-status-danger-border rounded-lg p-4 text-center">
            <p className="text-2xl font-bold">
              {result?.total_failed || 0}
            </p>
            <p className="text-sm">{t("resendInvitations.failures")}</p>
          </div>
        </div>

        {result && result.failed.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              {t("resendInvitations.failuresCount", { count: result.failed.length })}
            </p>
            <ScrollArea className="h-[150px]">
              <div className="space-y-2">
                {result.failed.map((fail, i) => (
                  <div key={i} className="text-xs bg-status-danger text-status-danger-foreground rounded p-2">
                    <p className="font-medium">{fail.email}</p>
                    <p>{fail.error}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            {t("resendInvitations.close")}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {step === "select" && renderSelectStep()}
        {step === "preview" && renderPreviewStep()}
        {step === "result" && renderResultStep()}
      </DialogContent>
    </Dialog>
  );
}
